import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../session/useSession";
import { useCreateAccommodationDraft } from "../../features/accommodations/ui/draftCreate";
import { DeferredAuthModal } from "../../features/auth/public";
import { isAppError } from "../../platform/http/errors";
import { clientLogger } from "../../platform/logging/clientLogger";
import { browserWindowNavigation } from "../../platform/browser/windowNavigation";
import {
  createAccommodationEditNavigationState,
  routeTo,
} from "../router/paths";
import {
  useNonModalOverlayRegistration,
  useOutsideClick,
} from "../../shared/ui";
import styles from "./UserMenu.module.css";

interface UserMenuProps {
  isLoggedIn: boolean;
}

type PendingMenuFocus = "first" | "last" | null;

const logDraftCreationError = (error: unknown) => {
  if (!isAppError(error)) return;

  clientLogger.error({
    message: "Accommodation draft creation failed.",
    error: {
      code: error.code,
      kind: error.kind,
      status: error.status,
    },
  });
};

const toDraftCreationErrorMessage = (error: unknown): string => {
  if (!isAppError(error)) {
    return "호스팅 준비를 시작하지 못했습니다. 잠시 후 다시 시도해주세요.";
  }

  switch (error.kind) {
    case "network":
      return "네트워크 연결을 확인한 뒤 다시 시도해주세요.";
    case "timeout":
      return "요청 시간이 초과되었습니다. 다시 시도해주세요.";
    case "authentication":
      return "로그인 상태를 확인한 뒤 다시 시도해주세요.";
    default:
      return "호스팅 준비를 시작하지 못했습니다. 잠시 후 다시 시도해주세요.";
  }
};

export const UserMenu: React.FC<UserMenuProps> = ({ isLoggedIn }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const menuHistoryEntry = {
    hash: location.hash,
    key: location.key,
    pathname: location.pathname,
    search: location.search,
  };
  const userMenuId = useId();
  const { logout } = useSession();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pendingMenuFocus, setPendingMenuFocus] =
    useState<PendingMenuFocus>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"login" | "signup">(
    "login",
  );
  const [draftCreateErrorMessage, setDraftCreateErrorMessage] = useState<
    string | null
  >(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuDropdownRef = useRef<HTMLDivElement>(null);
  const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const { createDraft, isCreating } = useCreateAccommodationDraft({
    onCreated: (accommodationId) => {
      if (!browserWindowNavigation.isCurrentHistoryEntry(menuHistoryEntry)) {
        return;
      }

      setDraftCreateErrorMessage(null);
      setIsMenuOpen(false);
      navigate(routeTo.accommodationEdit(accommodationId), {
        state: createAccommodationEditNavigationState(accommodationId),
      });
    },
    onError: (error) => {
      logDraftCreationError(error);
      if (!browserWindowNavigation.isCurrentHistoryEntry(menuHistoryEntry)) {
        return;
      }
      setDraftCreateErrorMessage(toDraftCreationErrorMessage(error));
      setPendingMenuFocus(null);
      setIsMenuOpen(true);
    },
  });

  useOutsideClick(menuRef, () => setIsMenuOpen(false), isMenuOpen);

  const closeMenuAndRestoreFocus = useCallback(() => {
    setPendingMenuFocus(null);
    setIsMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);
  const menuOverlay = useNonModalOverlayRegistration({
    enabled: isMenuOpen,
    onClose: closeMenuAndRestoreFocus,
    overlayRef: menuDropdownRef,
    restoreFocusOnClose: false,
    triggerRef: menuButtonRef,
  });

  const getMenuItems = () =>
    menuItemRefs.current.filter(
      (item): item is HTMLButtonElement => item !== null && !item.disabled,
    );

  const focusMenuItem = (index: number) => {
    const menuItems = getMenuItems();

    if (menuItems.length === 0) {
      return;
    }

    const nextIndex = (index + menuItems.length) % menuItems.length;
    menuItems[nextIndex]?.focus();
  };

  useEffect(() => {
    if (!isMenuOpen || !pendingMenuFocus) {
      return;
    }

    const menuItems = getMenuItems();
    const focusIndex = pendingMenuFocus === "first" ? 0 : menuItems.length - 1;

    menuItems[focusIndex]?.focus();
    setPendingMenuFocus(null);
  }, [isMenuOpen, pendingMenuFocus]);

  const openMenuAndFocus = (target: Exclude<PendingMenuFocus, null>) => {
    setPendingMenuFocus(target);
    setIsMenuOpen(true);
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.currentTarget === menuButtonRef.current && !isMenuOpen) {
      switch (event.key) {
        case "ArrowDown":
        case "Enter":
        case " ":
        case "Spacebar":
          event.preventDefault();
          openMenuAndFocus("first");
          return;
        case "ArrowUp":
          event.preventDefault();
          openMenuAndFocus("last");
          return;
        default:
          break;
      }
    }

    if (event.key === "Tab" && isMenuOpen) {
      setPendingMenuFocus(null);
      setIsMenuOpen(false);
      return;
    }

    const menuItems = getMenuItems();
    const currentIndex = menuItems.findIndex(
      (item) => item === document.activeElement,
    );

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusMenuItem(currentIndex >= 0 ? currentIndex + 1 : 0);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusMenuItem(
          currentIndex >= 0 ? currentIndex - 1 : menuItems.length - 1,
        );
        break;
      case "Home":
        event.preventDefault();
        focusMenuItem(0);
        break;
      case "End":
        event.preventDefault();
        focusMenuItem(menuItems.length - 1);
        break;
      case "Escape":
        if (menuOverlay.requestCloseOnEscape()) {
          event.preventDefault();
          event.stopPropagation();
        }
        break;
      default:
        break;
    }
  };

  const openAuthModal = (mode: "login" | "signup") => {
    closeMenuAndRestoreFocus();
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
  };

  const handleLogin = () => openAuthModal("login");

  const handleSignup = () => openAuthModal("signup");

  const handleWishlist = () => {
    navigate(routeTo.wishlist());
    setIsMenuOpen(false);
  };

  const handleProfile = () => {
    navigate(routeTo.profile());
    setIsMenuOpen(false);
  };

  const handleHosting = () => {
    if (isCreating) return;

    setDraftCreateErrorMessage(null);
    menuButtonRef.current?.focus();
    void createDraft();
  };

  const retryHosting = () => {
    setPendingMenuFocus(null);
    setIsMenuOpen(true);
    handleHosting();
  };

  const dismissDraftCreateError = () => {
    menuItemRefs.current[2]?.focus();
    setDraftCreateErrorMessage(null);
  };

  const handleLogout = async () => {
    try {
      const logoutOperation = logout();
      setIsMenuOpen(false);
      navigate(routeTo.home(), { replace: true, state: null });
      await logoutOperation;
    } catch (error) {
      clientLogger.error({ message: "Logout failed:", error });
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      <div className={styles.menuWrapper}>
        {/* 프로필 아이콘 - 로그인 시에만 표시 (왼쪽) */}
        {isLoggedIn && (
          <button
            aria-label="프로필"
            className={styles.profileButton}
            onClick={handleProfile}
            type="button"
          >
            <svg
              viewBox="0 0 32 32"
              fill="currentColor"
              className={styles.icon}
            >
              <path d="M16 15.503A5.041 5.041 0 1016 5.42a5.041 5.041 0 000 10.083zm0 2.215c-6.703 0-11 3.699-11 5.5V24h22v-1.782c0-1.801-4.297-5.5-11-5.5z" />
            </svg>
          </button>
        )}

        {/* 메뉴 아이콘 - 항상 표시 (오른쪽) */}
        <div className={styles.menuContainer} ref={menuRef}>
          <button
            aria-controls={isMenuOpen ? userMenuId : undefined}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            aria-label="사용자 메뉴"
            className={styles.menuButton}
            ref={menuButtonRef}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            onKeyDown={handleMenuKeyDown}
            type="button"
          >
            <svg
              viewBox="0 0 16 16"
              fill="currentColor"
              className={styles.icon}
            >
              <path d="M1 2.75A.75.75 0 011.75 2h12.5a.75.75 0 010 1.5H1.75A.75.75 0 011 2.75zm0 5A.75.75 0 011.75 7h12.5a.75.75 0 010 1.5H1.75A.75.75 0 011 7.75zM1.75 12a.75.75 0 000 1.5h12.5a.75.75 0 000-1.5H1.75z" />
            </svg>
          </button>

          {isMenuOpen && (
            <div
              aria-label="사용자 메뉴"
              className={styles.menuDropdown}
              id={userMenuId}
              onKeyDown={handleMenuKeyDown}
              ref={menuDropdownRef}
              role="menu"
              tabIndex={-1}
            >
              {!isLoggedIn ? (
                <>
                  <button
                    className={styles.menuItem}
                    onClick={handleLogin}
                    ref={(node) => {
                      menuItemRefs.current[0] = node;
                    }}
                    role="menuitem"
                    type="button"
                  >
                    로그인
                  </button>
                  <button
                    className={styles.menuItem}
                    onClick={handleSignup}
                    ref={(node) => {
                      menuItemRefs.current[1] = node;
                    }}
                    role="menuitem"
                    type="button"
                  >
                    회원가입
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={styles.menuItem}
                    onClick={handleWishlist}
                    ref={(node) => {
                      menuItemRefs.current[0] = node;
                    }}
                    role="menuitem"
                    type="button"
                  >
                    위시리스트
                  </button>
                  <button
                    className={styles.menuItem}
                    onClick={handleProfile}
                    ref={(node) => {
                      menuItemRefs.current[1] = node;
                    }}
                    role="menuitem"
                    type="button"
                  >
                    프로필
                  </button>
                  <button
                    className={styles.menuItem}
                    aria-busy={isCreating ? true : undefined}
                    disabled={isCreating}
                    onClick={handleHosting}
                    ref={(node) => {
                      menuItemRefs.current[2] = node;
                    }}
                    role="menuitem"
                    type="button"
                  >
                    <span>
                      {isCreating ? "호스팅 준비 중..." : "호스팅 하기"}
                    </span>
                    {isCreating && (
                      <span
                        aria-hidden="true"
                        className={styles.loadingIndicator}
                      />
                    )}
                  </button>
                  {isCreating && (
                    <div role="none">
                      <span className={styles.srOnly} role="status">
                        새 숙소를 위한 호스팅 공간을 준비하고 있습니다.
                      </span>
                    </div>
                  )}
                  {draftCreateErrorMessage && (
                    <div className={styles.draftError} role="none">
                      <p className={styles.draftErrorMessage} role="alert">
                        {draftCreateErrorMessage}
                      </p>
                      <div className={styles.draftErrorActions} role="none">
                        <button
                          className={styles.draftErrorAction}
                          onClick={retryHosting}
                          ref={(node) => {
                            menuItemRefs.current[3] = node;
                          }}
                          role="menuitem"
                          type="button"
                        >
                          다시 시도
                        </button>
                        <button
                          aria-label="호스팅 준비 오류 닫기"
                          className={styles.draftErrorAction}
                          onClick={dismissDraftCreateError}
                          ref={(node) => {
                            menuItemRefs.current[4] = node;
                          }}
                          role="menuitem"
                          type="button"
                        >
                          닫기
                        </button>
                      </div>
                    </div>
                  )}
                  <div
                    aria-orientation="horizontal"
                    className={styles.divider}
                    role="separator"
                  />
                  <button
                    className={styles.menuItem}
                    onClick={handleLogout}
                    ref={(node) => {
                      menuItemRefs.current[draftCreateErrorMessage ? 5 : 3] =
                        node;
                    }}
                    role="menuitem"
                    type="button"
                  >
                    로그아웃
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {isAuthModalOpen && (
        <DeferredAuthModal
          isOpen
          onClose={() => setIsAuthModalOpen(false)}
          initialMode={authModalMode}
        />
      )}
    </>
  );
};
